import React, { useState, useEffect } from 'react';
import { getDeals, createDeal, updateDeal, deleteDeal } from '../utils/api';
import { ar, en } from '../utils/translations';
import DualCalendarPicker from './DualCalendarPicker';

const DealsModal = ({ contactType, contactId, contactName, onClose, language = 'ar' }) => {
  const t = language === 'ar' ? ar : en;
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [formData, setFormData] = useState({
    property_type: '',
    property_address: '',
    deal_type: 'sale',
    price: '',
    deal_date: '',
    status: 'pending',
    notes: ''
  });

  useEffect(() => {
    loadDeals();
  }, [contactType, contactId]);

  const loadDeals = async () => {
    try {
      setLoading(true);
      const response = await getDeals(contactType, contactId);
      setDeals(response.data || []);
    } catch (error) {
      console.error('Error loading deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDeal) {
        await updateDeal(editingDeal.id, formData);
      } else {
        await createDeal({
          contact_type: contactType,
          contact_id: contactId,
          ...formData
        });
      }
      setFormData({
        property_type: '',
        property_address: '',
        deal_type: 'sale',
        price: '',
        deal_date: '',
        status: 'pending',
        notes: ''
      });
      setShowAddForm(false);
      setEditingDeal(null);
      loadDeals();
    } catch (error) {
      console.error('Error saving deal:', error);
      alert(t.failedToSave);
    }
  };

  const handleEdit = (deal) => {
    setEditingDeal(deal);
    setFormData({
      property_type: deal.property_type || '',
      property_address: deal.property_address || '',
      deal_type: deal.deal_type || 'sale',
      price: deal.price || '',
      deal_date: deal.deal_date || '',
      status: deal.status || 'pending',
      notes: deal.notes || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t.deleteDealConfirm || 'Are you sure you want to delete this deal?')) {
      try {
        await deleteDeal(id);
        loadDeals();
      } catch (error) {
        console.error('Error deleting deal:', error);
        alert(t.failedToDelete);
      }
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingDeal(null);
    setFormData({
      property_type: '',
      property_address: '',
      deal_type: 'sale',
      price: '',
      deal_date: '',
      status: 'pending',
      notes: ''
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h2>{t.deals} - {contactName}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        {!showAddForm ? (
          <div>
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>{t.closedDeals || 'Closed Deals & Transactions'}</h3>
              <button className="btn btn-primary" onClick={() => setShowAddForm(true)}>
                ➕ {t.addDeal || 'Add Deal'}
              </button>
            </div>
            
            {loading ? (
              <p>{t.loading}</p>
            ) : deals.length === 0 ? (
              <div className="empty-state">
                <p>{t.noDeals || 'No deals recorded yet. Click "Add Deal" to record a transaction.'}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {deals.map((deal) => (
                  <div key={deal.id} style={{
                    padding: '15px',
                    background: '#faf5f0',
                    borderRadius: '8px',
                    borderLeft: '4px solid #ffd4a3'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: '1.1rem' }}>
                            {deal.property_type || t.propertyType || 'Property'} - {deal.deal_type === 'sale' ? (t.sale || 'Sale') : (t.rent || 'Rent')}
                          </strong>
                          <span style={{ 
                            fontSize: '0.75rem', 
                            padding: '2px 8px', 
                            background: deal.status === 'completed' ? '#d4edda' : '#fff3cd',
                            borderRadius: '12px',
                            fontWeight: '500'
                          }}>
                            {deal.status === 'completed' ? (t.completed || 'Completed') : (t.pending || 'Pending')}
                          </span>
                        </div>
                        {deal.property_address && (
                          <p style={{ margin: '4px 0', fontSize: '0.9rem', opacity: 0.8 }}>
                            📍 {deal.property_address}
                          </p>
                        )}
                        {deal.price && (
                          <p style={{ margin: '4px 0', fontSize: '1rem', fontWeight: '600', color: '#000000' }}>
                            💰 {deal.price}
                          </p>
                        )}
                        {deal.deal_date && (
                          <p style={{ margin: '4px 0', fontSize: '0.85rem', opacity: 0.6 }}>
                            📅 {new Date(deal.deal_date).toLocaleDateString(language === 'ar' ? 'ar-SA' : 'en-US')}
                          </p>
                        )}
                        {deal.notes && (
                          <p style={{ margin: '8px 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                            {deal.notes}
                          </p>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px', marginLeft: '10px' }}>
                        <button
                          onClick={() => handleEdit(deal)}
                          style={{
                            padding: '6px 12px',
                            background: '#ffffff',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85rem'
                          }}
                        >
                          {t.edit}
                        </button>
                        <button
                          onClick={() => handleDelete(deal.id)}
                          style={{
                            padding: '6px 12px',
                            background: '#ffffff',
                            border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            color: '#dc3545'
                          }}
                        >
                          {t.delete}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h3 style={{ marginBottom: '20px' }}>
              {editingDeal ? (t.editDeal || 'Edit Deal') : (t.addDeal || 'Add New Deal')}
            </h3>
            
            <div className="form-group">
              <label>{t.propertyType || 'Property Type'}</label>
              <select
                name="property_type"
                value={formData.property_type}
                onChange={handleChange}
              >
                <option value="">{t.selectPropertyType || 'Select...'}</option>
                <option value="apartment">{t.apartment || 'Apartment'}</option>
                <option value="villa">{t.villa || 'Villa'}</option>
                <option value="land">{t.land || 'Land'}</option>
                <option value="commercial">{t.commercial || 'Commercial'}</option>
                <option value="office">{t.office || 'Office'}</option>
                <option value="other">{t.other || 'Other'}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t.propertyAddress || 'Property Address'}</label>
              <input
                type="text"
                name="property_address"
                value={formData.property_address}
                onChange={handleChange}
                placeholder={t.propertyAddressPlaceholder || 'Enter property address'}
              />
            </div>

            <div className="form-group">
              <label>{t.dealType || 'Deal Type'}</label>
              <select
                name="deal_type"
                value={formData.deal_type}
                onChange={handleChange}
              >
                <option value="sale">{t.sale || 'Sale'}</option>
                <option value="rent">{t.rent || 'Rent'}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t.price || 'Price'}</label>
              <input
                type="text"
                name="price"
                value={formData.price}
                onChange={handleChange}
                placeholder={t.pricePlaceholder || 'e.g., 500,000 SAR'}
              />
            </div>

            <div className="form-group">
              <label>{t.dealDate || 'Deal Date'}</label>
              <DualCalendarPicker
                value={formData.deal_date}
                onChange={(v) => setFormData((prev) => ({ ...prev, deal_date: v }))}
                language={language}
                placeholder={language === 'ar' ? 'اختر تاريخ الصفقة' : 'Pick deal date'}
              />
            </div>

            <div className="form-group">
              <label>{t.status}</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="pending">{t.pending || 'Pending'}</option>
                <option value="completed">{t.completed || 'Completed'}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t.notes}</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                placeholder={t.dealNotesPlaceholder || 'Additional notes about this deal...'}
              />
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                {t.cancel}
              </button>
              <button type="submit" className="btn btn-primary">
                {t.save}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default DealsModal;

